import { isEmpty } from 'lodash-es';
import { paramCleaner } from '@/helpers/formatters';
import { getQuarter } from '@/helpers/date';
import { format } from 'date-fns';
import { postRequest, formSubmitRequest, postMultipartRequest } from './request';

export const getExecutiveInsights = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => {
  const {
    to: queryTo, from: queryFrom, owned: queryOwned, ...paramFilters
  } = routeQuery;

  const cleanedParams = paramCleaner(paramFilters);

  const { to = queryTo, from = queryFrom } = selectedDateRange;

  const searchParams = new URLSearchParams();

  if (typeof to !== 'undefined') searchParams.append('to', to);
  else searchParams.append('to', format(getQuarter(1)[1], 'yyyy-MM-dd'));

  if (typeof from !== 'undefined') searchParams.append('from', from);
  else searchParams.append('from', format(getQuarter(1)[0], 'yyyy-MM-dd'));

  return postRequest({
    url: `/ae/process-insights/executive-insights?${searchParams}`,
    timeout: 60000,
    body: {
      filters: !isEmpty(filters) ? filters : cleanedParams,
      dateRange: selectedDateRange,
      owned,
    },
  });
};

export const complianceReport = async ({
  params, selectedFilters = {}, selectedMyJobs, sort,
}) => {
  const {
    page, to, from, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams();
  if (typeof page !== 'undefined') searchParams.append('page', page);
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);

  const body = {
    filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
    owned: selectedMyJobs,
  };

  const { reportingShareKey } = window.sessionStorage;

  if (reportingShareKey) {
    body.key = reportingShareKey;
  }

  const {
    documents = [],
    filters = [],
    pagination = {},
    totals = {},
    owned = {},
    sorting = {},
    exclusions = {},
    projectImageUrl,
    projectName,
  } = await postRequest({
    url: `/analytics/compliance?${searchParams}`,
    body,
  });
  return {
    documents,
    filters,
    pagination,
    totals,
    owned,
    sorting,
    exclusions,
    projectImageUrl,
    projectName,
  };
};

export const employerBrandReport = async ({
  params, selectedFilters = {}, selectedMyJobs, sort, isTemplate,
}) => {
  const {
    page, to, from, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams();
  if (typeof page !== 'undefined') searchParams.append('page', page);
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);

  const body = {
    filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
    owned: selectedMyJobs,
    isTemplate,
  };

  const { reportingShareKey } = window.sessionStorage;

  if (reportingShareKey) {
    body.key = reportingShareKey;
  }

  const {
    documents = [],
    filters = [],
    pagination = {},
    totals = {},
    owned = {},
    sorting = {},
    exclusions = {},
    projectImageUrl,
    projectName,
  } = await postRequest({
    url: `/analytics/employer-brand?${searchParams}`,
    body,
  });
  return {
    documents,
    filters,
    pagination,
    totals,
    owned,
    sorting,
    exclusions,
    projectImageUrl,
    projectName,
  };
};

export const jobPerformanceReport = async ({
  params, selectedFilters = {}, selectedMyJobs, sort,
}) => {
  const {
    page, to, from, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams();
  if (typeof page !== 'undefined') searchParams.append('page', page);
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);

  const body = {
    filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
    owned: selectedMyJobs,
  };

  const { reportingShareKey } = window.sessionStorage;

  if (reportingShareKey) {
    body.key = reportingShareKey;
  }

  const {
    documents = [],
    filters = [],
    pagination = {},
    totals = {},
    owned = {},
    sorting = {},
    projectImageUrl,
    projectName,
  } = await postRequest({
    url: `/analytics/job-performance?${searchParams}`,
    body,
  });
  return {
    documents,
    filters,
    pagination,
    totals,
    owned,
    sorting,
    projectImageUrl,
    projectName,
  };
};

export const reportingUsage = async ({
  params, selectedFilters = {}, selectedDateRange = {}, selectedMyJobs,
}) => {
  const {
    page, to, from, is_post_revisions_score: isPostRevisionsScore, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams();
  if (typeof page !== 'undefined') searchParams.append('page', page);

  const {
    dateChart = [],
    dateRange = {},
    documents = [],
    engagementUsers = [],
    filters = [],
    pagination = {},
    scoreMatrix = [],
    totals = {},
    owned = {},
  } = await postRequest({
    url: `/analytics/usage?${searchParams}`,
    body: {
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
      dateRange: !isEmpty(selectedDateRange) ? selectedDateRange : { to, from },
      owned: selectedMyJobs,
    },
  });
  return {
    dateChart,
    dateRange,
    documents,
    engagementUsers,
    filters,
    pagination,
    scoreMatrix,
    totals,
    owned,
  };
};

export const reportingDownloadUsage = ({
  document, filters, dateRange, myJobsEnabled,
}) => {
  const { to, from } = dateRange;

  formSubmitRequest(document, '/analytics/usage-download', {
    filters,
    dateRange: { to, from },
    owned: myJobsEnabled,
  });
};

export const sendDashboardShareLink = async (body) => postRequest({
  url: '/analytics/dashboard-share-link',
  body,
});

export const sendDashboardShareImage = async (formData) => postMultipartRequest({
  url: '/analytics/dashboard-share-image',
  body: formData,
});

export const sendActionableInsightsShareLinks = async ({
  userId, note, selectedJobs, reportUrl, reportId, scheduledAt,
}) => {
  try {
    const { success, succeeded, failed } = await postRequest({
      url: '/analytics/notify',
      body: {
        userId, note, selectedJobs, reportUrl, reportId, scheduledAt,
      },
    });

    /* If all emails sent successfully: success = true */
    if (success) {
      return { success, succeeded, failed };
    }
  } catch (error) {
    const {
      failed, succeeded, success, attributes, errors, code,
    } = error.response || {};

    /* If some emails sent successfully, but others failed:
      success = false,
      succeeded = array of email address that were successfully sent to,
      failed = array of email address that could not be sent to,
    */
    if (!success && succeeded && failed) {
      return { failed, succeeded, success };
    }

    if (code === 'validation_failed') {
      return { attributes, errors, code };
    }
  }
  throw new Error('Email failed');
};

export const complianceReportDownload = ({
  document, filters, dateRange, myJobsEnabled, sort,
}) => formSubmitRequest(document, `/analytics/compliance-download?sort=${sort}`, {
  filters,
  dateRange,
  owned: myJobsEnabled,
});

export const employerBrandReportDownload = ({
  document, filters, dateRange, myJobsEnabled, sort, isTemplate,
}) => formSubmitRequest(document, `/analytics/employer-brand-download?sort=${sort}`, {
  filters,
  dateRange,
  owned: myJobsEnabled,
  isTemplate,
});

export const jobPerformanceReportDownload = ({
  document, filters, dateRange, myJobsEnabled, sort,
}) => formSubmitRequest(document, `/analytics/job-performance-download?sort=${sort}`, {
  filters,
  dateRange,
  owned: myJobsEnabled,
});

export const getTooltips = async (keys) => postRequest({
  url: '/alt/tooltips/any',
  body: {
    keys,
  },
});

export const contentSnapshot = async ({
  selectedFilters = {}, selectedDateWeek = '', selectedMyJobs, params = {},
}) => {
  const {
    week: paramWeek, owned: paramOwned, key: paramKey, ...filterParams
  } = params;
  const cleanedFilters = paramCleaner(filterParams);

  const body = {
    filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedFilters,
    dateWeek: selectedDateWeek,
    owned: selectedMyJobs,
  };

  const { reportingShareKey } = window.sessionStorage;

  if (reportingShareKey) {
    body.key = reportingShareKey;
  }

  const {
    filters = [],
    dateWeek,
    owned = {},
    contentStats,
    totals,
    projectImageUrl,
    projectName,
  } = await postRequest({
    url: '/analytics/content',
    body,
  });
  return {
    filters, dateWeek, owned, contentStats, totals, projectImageUrl, projectName,
  };
};

export const weeklySnapshot = async ({
  selectedFilters = {}, week = '', owned: selectedMyJobs, page = 1, sort = 'title', params = {},
}) => {
  const {
    week: paramWeek, page: paramPage, sort: paramSort, owned: paramOwned, ...filterParams
  } = params;
  const cleanedFilters = paramCleaner(filterParams);

  const {
    filters = [],
    dateWeek,
    owned = {},
    pagination = {},
    documents = [],
    totals = {},
  } = await postRequest({
    url: `/analytics/health?page=${page}&sort=${sort}`,
    body: {
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedFilters,
      dateWeek: week,
      owned: selectedMyJobs,
    },
  });

  return {
    filters, dateWeek, owned, documents, pagination, totals,
  };
};

export const weeklySnapshotDownload = ({
  document, filters, dateWeek, myJobsEnabled, sort = 'title',
}) => formSubmitRequest(document, `/analytics/health-download?sort=${sort}`, {
  filters,
  dateWeek,
  owned: myJobsEnabled,
});

export const recruitingPerformance = async ({
  selectedFilters = {}, selectedDateRange = {}, selectedMyJobs, page = 1, params = {},
}) => {
  const {
    week: paramWeek, from, to, page: paramPage, sort: paramSort, owned: paramOwned, ...filterParams
  } = params;
  const cleanedFilters = paramCleaner(filterParams);

  const body = {
    filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedFilters,
    dateRange: selectedDateRange,
    owned: selectedMyJobs,
  };

  const { reportingShareKey } = window.sessionStorage;

  if (reportingShareKey) {
    body.key = reportingShareKey;
  }

  const {
    filters = [],
    dateRange = {},
    owned = {},
    periodData = {},
    pagination = {},
    documents = [],
    totals = {},
    summary = {},
    projectImageUrl,
    projectName,
  } = await postRequest({
    url: `/analytics/recruiting-performance?page=${page}`,
    body,
  });

  return {
    filters,
    dateRange,
    owned,
    pagination,
    documents,
    totals,
    periodData,
    summary,
    projectImageUrl,
    projectName,
  };
};

export const languageSnapshot = async ({
  selectedFilters = {}, selectedDateWeek = '', selectedMyJobs, params = {},
}) => {
  const {
    week: paramWeek, owned: paramOwned, key: paramKey, ...filterParams
  } = params;
  const cleanedFilters = paramCleaner(filterParams);

  const body = {
    filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedFilters,
    dateWeek: selectedDateWeek,
    owned: selectedMyJobs,
  };

  const { reportingShareKey } = window.sessionStorage;

  if (reportingShareKey) {
    body.key = reportingShareKey;
  }

  const {
    filters = [],
    dateWeek,
    owned = {},
    languageBlocks,
    totals,
    projectImageUrl,
    projectName,
  } = await postRequest({
    url: '/analytics/language',
    body,
  });

  return {
    filters, dateWeek, owned, languageBlocks, totals, projectImageUrl, projectName,
  };
};

export const fetchGenderReport = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => {
  const url = '/ae/process-insights/gender';

  const {
    to: queryTo, from: queryFrom, owned: queryOwned, ...paramFilters
  } = routeQuery;

  const cleanedParams = paramCleaner(paramFilters);

  return postRequest({
    url,
    body: {
      filters: !isEmpty(filters) ? filters : cleanedParams,
      dateRange: selectedDateRange,
      owned,
    },
    timeout: 60000,
  });
};

export const fetchOverallReport = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => {
  const url = '/ae/source-insights/overall';

  const {
    to: queryTo, from: queryFrom, owned: queryOwned, ...paramFilters
  } = routeQuery;

  const cleanedParams = paramCleaner(paramFilters);

  return postRequest({
    url,
    body: {
      filters: !isEmpty(filters) ? filters : cleanedParams,
      dateRange: selectedDateRange,
      owned,
    },
    timeout: 60000,
  });
};

export const fetchOutboundReport = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => {
  const url = '/ae/source-insights/outbound';

  const {
    to: queryTo, from: queryFrom, owned: queryOwned, ...paramFilters
  } = routeQuery;

  const cleanedParams = paramCleaner(paramFilters);

  return postRequest({
    url,
    body: {
      filters: !isEmpty(filters) ? filters : cleanedParams,
      dateRange: selectedDateRange,
      owned,
    },
    timeout: 60000,
  });
};

export const fetchInboundReport = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned, isGeneralVsNiche = false,
}) => {
  const url = '/ae/source-insights/inbound';

  const {
    to: queryTo,
    from: queryFrom,
    owned: queryOwned,
    general_vs_niche: generalVsNiche,
    ...paramFilters
  } = routeQuery;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams();

  searchParams.append('source_group', isGeneralVsNiche ? 'general_vs_niche' : 'free_vs_paid');

  return postRequest({
    url: `${url}?${searchParams}`,
    body: {
      filters: !isEmpty(filters) ? filters : cleanedParams,
      dateRange: selectedDateRange,
      owned,
    },
    timeout: 60000,
  });
};

export const fetchHiringReport = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => {
  const url = '/ae/process-insights/hiring';

  const {
    to: queryTo, from: queryFrom, owned: queryOwned, ...paramFilters
  } = routeQuery;

  const cleanedParams = paramCleaner(paramFilters);

  return postRequest({
    url,
    body: {
      filters: !isEmpty(filters) ? filters : cleanedParams,
      dateRange: selectedDateRange,
      owned,
    },
    timeout: 60000,
  });
};

export const fetchPassthroughReport = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => {
  const url = '/ae/process-insights/passthrough';

  const {
    to: queryTo, from: queryFrom, owned: queryOwned, ...paramFilters
  } = routeQuery;

  const cleanedParams = paramCleaner(paramFilters);

  return postRequest({
    url,
    body: {
      filters: !isEmpty(filters) ? filters : cleanedParams,
      dateRange: selectedDateRange,
      owned,
    },
    timeout: 60000,
  });
};

export const fetchDropoffReport = async ({
  selectedDateRange = {}, routeQuery = {}, filters = [], owned,
}) => {
  const url = '/ae/process-insights/dropoff';

  const {
    to: queryTo, from: queryFrom, owned: queryOwned, ...paramFilters
  } = routeQuery;

  const cleanedParams = paramCleaner(paramFilters);

  return postRequest({
    url,
    body: {
      filters: !isEmpty(filters) ? filters : cleanedParams,
      dateRange: selectedDateRange,
      owned,
    },
    timeout: 60000,
  });
};
